// Background films should only load and decode while the visitor can see them.
if (!customElements.get('gh-background-video')) {
  customElements.define('gh-background-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.abort = new AbortController();
      this.visible = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      const options = { signal: this.abort.signal };
      const update = () => this.updatePlayback();
      document.addEventListener('visibilitychange', update, options);
      window.addEventListener('pagehide', () => this.video.pause(), options);
      window.addEventListener('pageshow', update, options);
      this.motion.addEventListener('change', update, options);
      this.connection?.addEventListener('change', update, options);
      this.video.addEventListener('loadeddata', () => this.revealFirstFrame(), options);
      this.video.addEventListener('emptied', () => this.resetPoster(), options);
      this.video.addEventListener('error', () => this.resetPoster(), options);
      if (this.video.readyState >= 2) this.revealFirstFrame();

      // Observe the video: the display:contents wrapper has no layout box.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio > 0;
          this.updatePlayback();
        });
        this.observer.observe(this.video);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.video?.pause();
      this.visible = false;
      this.cancelFrameReveal();
    }

    cancelFrameReveal() {
      if (this.frameCallback != null) {
        this.video.cancelVideoFrameCallback(this.frameCallback);
        this.frameCallback = null;
      }
      cancelAnimationFrame(this.revealAnimation);
    }

    resetPoster() {
      this.cancelFrameReveal();
      this.removeAttribute('data-first-frame');
    }

    revealFirstFrame() {
      if (this.hasAttribute('data-first-frame') || this.frameCallback != null) return;
      const reveal = () => {
        this.frameCallback = null;
        this.revealAnimation = requestAnimationFrame(() => {
          if (this.isConnected && this.video.readyState >= 2) {
            this.setAttribute('data-first-frame', '');
          }
        });
      };
      if ('requestVideoFrameCallback' in this.video) {
        this.frameCallback = this.video.requestVideoFrameCallback(reveal);
      } else {
        // Older engines: allow a full paint after current-frame data arrives.
        this.revealAnimation = requestAnimationFrame(reveal);
      }
    }

    shouldPlay() {
      return this.isConnected && this.visible && !document.hidden &&
        !this.motion.matches && !this.connection?.saveData;
    }

    updatePlayback() {
      if (!this.shouldPlay()) {
        this.video.pause();
        return;
      }
      if (this.pendingPlay || !this.video.paused) return;

      const sources = this.video.querySelectorAll('source[data-src]');
      if (sources.length) {
        for (const source of sources) {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        }
        this.video.load();
      }
      this.video.muted = true;
      this.pendingPlay = true;
      this.video.play().catch(() => {
        // Autoplay restrictions and network failures leave the existing poster.
      }).finally(() => {
        this.pendingPlay = false;
        // Scrolling or switching tabs can happen before play() settles.
        if (!this.shouldPlay()) this.video.pause();
      });
    }
  });
}
